import {Paths} from "../types/Paths";

// Readers are pure functions of the path (they capture no model), so one global
// cache serves every object. Paths come from code/schemas, so the set is finite.
const pathReaderCache = new Map<string, (model: any) => any>();

function createPathReader(path: string): (model: any) => any {
	if (!path) {
		return (model: any) => model;
	}

	if (path.indexOf(".") === -1) {
		// Single property optimization
		return (model: any) => model == null ? undefined : model[path];
	}

	// Plain bracket access invokes prototype getters with normal semantics, so the reader
	// stays correct even when the object at a segment is later replaced by an instance of
	// a different class. (Caching getter functions per path — the previous approach — went
	// stale on exactly that class switch.)
	const keys = path.split(".");
	return (model: any) => {
		let current = model;
		for (let i = 0; i < keys.length; i++) {
			if (current == null) return undefined;
			current = current[keys[i]];
		}
		return current;
	};
}

/**
 * Reads a nested property from an object using a dot notation path (e.g., "user.address.city").
 * Supports both plain properties and getter functions.
 * Uses caching for performance with repeated accesses to the same paths.
 * @param model The object to read from.
 * @param path The dot-notation path to the property (e.g., "user.address.city").
 * @returns The value at the specified path, or undefined if any intermediate property is null/undefined.
 */
export default function readNestedProperty<T>(model: T, path: Paths<T>): any {
	if (!path || !model) {
		return model;
	}

	let readFunction = pathReaderCache.get(path);
	if (!readFunction) {
		// MVVM tracking paths can carry array indices ("items.3.name"), so the key space
		// isn't strictly finite; a full reset past the bound keeps the session leak-free
		// and recompiling a reader is trivial. ponytail: swap for LRU if this ever thrashes.
		if (pathReaderCache.size >= 10_000) {
			pathReaderCache.clear();
		}
		readFunction = createPathReader(path);
		pathReaderCache.set(path, readFunction);
	}

	return readFunction(model);
}
